import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Globe, 
  Key,
  Plus,
  Trash2,
  Copy,
  Check,
  Loader2,
  Shield,
  Eye,
  EyeOff
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsPage = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyDescription, setNewKeyDescription] = useState("");
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showKey, setShowKey] = useState(false);
  
  // Confidence settings
  const [confidenceThreshold, setConfidenceThreshold] = useState(85);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    fetchApiKeys();
    fetchConfidenceSettings();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`${API}/auth/keys`);
      setApiKeys(response.data.keys || []);
    } catch (error) {
      console.error("Failed to fetch API keys:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConfidenceSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/confidence`);
      setConfidenceThreshold(Math.round(response.data.threshold * 100));
    } catch (error) {
      console.error("Failed to fetch confidence settings:", error);
    }
  };

  const createApiKey = async () => {
    if (!newKeyName.trim()) {
      toast.error("Please enter a name for the API key");
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/auth/keys`, {
        name: newKeyName,
        description: newKeyDescription
      });
      
      setNewlyCreatedKey(response.data.key_prefix);
      await fetchApiKeys();
      toast.success("API key created successfully");
    } catch (error) {
      console.error("Failed to create API key:", error);
      toast.error("Failed to create API key");
    } finally {
      setCreating(false);
    }
  };

  const revokeApiKey = async (keyId) => {
    try {
      await axios.delete(`${API}/auth/keys/${keyId}`);
      await fetchApiKeys();
      toast.success("API key revoked");
    } catch (error) {
      console.error("Failed to revoke API key:", error);
      toast.error("Failed to revoke API key");
    }
  };

  const copyToClipboard = async (text, keyId) => {
    await navigator.clipboard.writeText(text);
    setCopiedKey(keyId);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const saveConfidenceSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/settings/confidence`, {
        threshold: confidenceThreshold / 100,
        alert_enabled: true
      });
      toast.success("Confidence threshold updated");
    } catch (error) {
      console.error("Failed to save settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleDateString("en-CA", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const resetDialog = () => {
    setNewKeyName("");
    setNewKeyDescription("");
    setNewlyCreatedKey(null);
    setShowKey(false);
  };

  return (
    <div className="min-h-screen pb-12 bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <Globe className="w-6 h-6 text-primary" />
            <span className="font-heading text-xl font-bold tracking-tight text-foreground">MeteoTranslate</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/translate" className="nav-link text-sm font-medium" data-testid="nav-translate">
              Translate
            </Link>
            <Link to="/terminology" className="nav-link text-sm font-medium" data-testid="nav-terminology">
              Terminology
            </Link>
            <Link to="/metrics" className="nav-link text-sm font-medium" data-testid="nav-metrics">
              Metrics
            </Link>
            <Link to="/analytics" className="nav-link text-sm font-medium" data-testid="nav-analytics">
              Analytics
            </Link>
            <Link to="/history" className="nav-link text-sm font-medium" data-testid="nav-history">
              History
            </Link>
            <Link to="/settings" className="nav-link active text-sm font-medium" data-testid="nav-settings">
              Settings
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 pt-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-5 h-5 text-primary" />
            <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground" data-testid="page-title">
              Settings
            </h1>
          </div>
          <p className="text-muted-foreground">
            Manage API keys and configure translation settings
          </p>
        </div>

        {/* Confidence Threshold Settings */}
        <div className="bg-white rounded-lg border border-border p-6 mb-8 shadow-card">
          <h2 className="font-heading text-xl font-bold mb-4 text-foreground">Confidence Threshold</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Translations below this threshold will be flagged for review.
          </p>
          
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="threshold">Threshold</Label>
                <span className="font-mono text-sm font-bold text-primary">{confidenceThreshold}%</span>
              </div>
              <input
                id="threshold"
                type="range"
                min="50"
                max="95"
                step="5"
                value={confidenceThreshold}
                onChange={(e) => setConfidenceThreshold(parseInt(e.target.value))}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>50%</span>
                <span>70%</span>
                <span>85%</span>
                <span>95%</span>
              </div>
            </div>
            <Button 
              onClick={saveConfidenceSettings}
              disabled={savingSettings}
              className="bg-primary hover:bg-primary-hover text-white"
            >
              {savingSettings ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Save"
              )}
            </Button>
          </div>
        </div>

        {/* API Keys Section */}
        <div className="bg-white rounded-lg border border-border p-6 shadow-card">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground">API Keys</h2>
              <p className="text-sm text-muted-foreground">
                Manage API keys for M2M authentication
              </p>
            </div>
            
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetDialog(); }}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary-hover text-white" data-testid="create-key-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Key
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-border">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">
                    {newlyCreatedKey ? "API Key Created" : "Create New API Key"}
                  </DialogTitle>
                  <DialogDescription>
                    {newlyCreatedKey 
                      ? "Copy your API key now. You won't be able to see it again."
                      : "Create a new API key for programmatic access to the translation API."}
                  </DialogDescription>
                </DialogHeader>
                
                {newlyCreatedKey ? (
                  <div className="space-y-4 my-4">
                    <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <Label className="text-sm text-emerald-700 mb-2 block">Your API Key</Label>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 p-2 bg-white rounded border text-sm font-mono break-all">
                          {showKey ? newlyCreatedKey : '•'.repeat(40)}
                        </code>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowKey(!showKey)}
                        >
                          {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(newlyCreatedKey, 'new')}
                        >
                          {copiedKey === 'new' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Store this key securely. It will only be shown once.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4 my-4">
                    <div>
                      <Label htmlFor="key-name">Name *</Label>
                      <Input
                        id="key-name"
                        value={newKeyName}
                        onChange={(e) => setNewKeyName(e.target.value)}
                        placeholder="e.g., Production API"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="key-description">Description</Label>
                      <Input
                        id="key-description"
                        value={newKeyDescription}
                        onChange={(e) => setNewKeyDescription(e.target.value)}
                        placeholder="Optional description"
                        className="mt-1"
                      />
                    </div>
                  </div>
                )}
                
                <DialogFooter>
                  {newlyCreatedKey ? (
                    <Button onClick={() => { setDialogOpen(false); resetDialog(); }}>
                      Done
                    </Button>
                  ) : (
                    <Button 
                      onClick={createApiKey}
                      disabled={creating}
                      className="bg-primary hover:bg-primary-hover text-white"
                    >
                      {creating ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <Key className="w-4 h-4 mr-2" />
                          Create API Key
                        </>
                      )}
                    </Button>
                  )}
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {/* Keys List */}
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="p-4 border border-border rounded-lg">
                  <div className="skeleton h-5 w-32 rounded mb-2" />
                  <div className="skeleton h-4 w-48 rounded" />
                </div>
              ))}
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Key className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No API keys yet. Create one to enable M2M authentication.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {apiKeys.map((key) => (
                <div 
                  key={key.id} 
                  className={`p-4 border rounded-lg ${key.is_active ? 'border-border' : 'border-red-200 bg-red-50'}`}
                  data-testid={`api-key-${key.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">{key.name}</h3>
                        {!key.is_active && (
                          <span className="px-2 py-0.5 text-xs bg-red-100 text-red-600 rounded">Revoked</span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {key.description || "No description"}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span>Created: {formatDate(key.created_at)}</span>
                        <span>Last used: {formatDate(key.last_used)}</span>
                      </div>
                      <code className="text-xs font-mono text-muted-foreground mt-2 block">
                        {key.key_prefix}
                      </code>
                    </div>
                    
                    {key.is_active && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-white border-border">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Revoke API Key?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will immediately disable this API key. Any applications using it will lose access.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => revokeApiKey(key.id)}
                              className="bg-destructive text-white"
                            >
                              Revoke Key
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Usage Info */}
          <div className="mt-6 p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium mb-2">How to use API keys</h4>
            <p className="text-sm text-muted-foreground mb-2">
              Include your API key in the request header:
            </p>
            <code className="block p-2 bg-white rounded border text-xs font-mono">
              X-API-Key: mt_your_api_key_here
            </code>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
